import { CommonNumericConstants } from 'vrlcm-gui-constants';
import {
  AllConfigParams,
  LoginConfig,
  LoginConfigName,
  Request,
  UiLoginConfig,
  UiUserData,
  User
} from 'vrlcm-gui-types';
import { Http, utf8_to_b64 } from 'vrlcm-gui-utils';
import { AuthznApiUri } from './uri';

interface AuthCache {
  user?: User;
}

function findConfigValue(
  configname: LoginConfigName,
  configs: LoginConfig[]
): string | undefined {
  const config = configs.find(c => c.configname === configname);
  return config ? config.configvalue : undefined;
}

export class AuthApi {
  private static authCache: AuthCache = {};

  public static async whoAmI() {
    const { user } = AuthApi.authCache;

    if (user) {
      return user;
    }
    const response = await Http.get<User>(AuthznApiUri.WHO_AM_I_URL);
    AuthApi.authCache.user = response.data;
    return response.data;
  }

  public static async getLoginConfigFromApi() {
    const response = await Http.get<LoginConfig[]>(
      AuthznApiUri.GET_LOGIN_CONFIG_URL
    );
    return response.data;
  }

  public static async getUiLoginConfig() {
    try {
      const loginConfigs = await this.getLoginConfigFromApi();
      const uiLoginConfig: UiLoginConfig = {
        vidmRegistered: loginConfigs.some(
          c =>
            c.configname === 'lcm.vidm.registered' && c.configvalue === 'true'
        ),
        firstbootAdminPasswordChanged: loginConfigs.some(
          c =>
            c.configname === 'lcm.firstboot.admin.password.changed' &&
            c.configvalue === 'true'
        ),
        firstbootVcfAdminPasswordChanged: loginConfigs.some(
          c =>
            c.configname === 'lcm.firstboot.vcfadmin.password.changed' &&
            c.configvalue === 'true'
        ),
        vcfMode: loginConfigs.some(
          c =>
            c.configname === 'lcm.vcf.mode.enabled' && c.configvalue === 'true'
        ),
        vcfDebugMode: loginConfigs.some(
          c =>
            c.configname === 'lcm.vcf.mode.debug.enabled' &&
            c.configvalue === 'true'
        )
      };
      return uiLoginConfig;
    } catch (e) {
      const uiLoginConfig: UiLoginConfig = {
        vidmRegistered: false,
        firstbootAdminPasswordChanged: false,
        firstbootVcfAdminPasswordChanged: false,
        vcfDebugMode: false,
        vcfMode: false
      };
      return uiLoginConfig;
    }
  }

  /**
   * pre-condition, authentication must be completed
   */
  public static async getAllConfigParams(): Promise<AllConfigParams> {
    const response = await Http.get<LoginConfig[]>(
      AuthznApiUri.GET_ALL_CONFIG_PARAMS_URL
    );
    const configs = response.data;
    const allConfigs: AllConfigParams = {
      vidmRegistered: configs.some(
        c => c.configname === 'lcm.vidm.registered' && c.configvalue === 'true'
      ),
      firstbootAdminPasswordChanged: configs.some(
        c =>
          c.configname === 'lcm.firstboot.admin.password.changed' &&
          c.configvalue === 'true'
      ),
      firstbootVcfAdminPasswordChanged: configs.some(
        c =>
          c.configname === 'lcm.firstboot.vcfadmin.password.changed' &&
          c.configvalue === 'true'
      ),
      vcfMode: configs.some(
        c => c.configname === 'lcm.vcf.mode.enabled' && c.configvalue === 'true'
      ),
      vcfDebugMode: configs.some(
        c =>
          c.configname === 'lcm.vcf.mode.debug.enabled' &&
          c.configvalue === 'true'
      ),
      lcmVidmClientId: findConfigValue('lcm.vidm.clientId', configs),
      lcmVidmClientSecret: findConfigValue('lcm.vidm.clientSecret', configs),
      lcmHostname: findConfigValue('lcm.hostname', configs),
      lcmVidmCatalogAppId: findConfigValue('lcm.vidm.catalog.app.id', configs),
      lcmVidmCatalogAppName: findConfigValue(
        'lcm.vidm.catalog.app.name',
        configs
      )
    };
    return allConfigs;
  }

  public static async getConfigByName(
    configName: LoginConfigName
  ): Promise<{
    value?: string;
    exists?: boolean;
  }> {
    try {
      const { data: config } = await Http.get<LoginConfig>(
        AuthznApiUri.GET_CONFIG_BY_NAME_URL(configName)
      );
      return {
        value: config.configvalue,
        exists: true
      };
    } catch (e) {
      return {
        exists: false
      };
    }
  }

  public static async createOrUpdateConfig(
    configname: LoginConfigName,
    configvalue: string
  ): Promise<any> {
    const { exists } = await this.getConfigByName(configname);
    const apiBody = {
      configname: configname,
      configvalue: configvalue
    };
    if (exists) {
      return await Http.patch(AuthznApiUri.CONFIG_PARAMS_BASE_URL, apiBody);
    } else {
      return await Http.post(AuthznApiUri.CONFIG_PARAMS_BASE_URL, apiBody);
    }
  }

  public static async login(username: string, password: string) {
    await Http.post(AuthznApiUri.LOGIN_URL, undefined, {
      headers: {
        Authorization: `Basic ${utf8_to_b64(username + ':' + password)}`
      }
    });
  }

  public static async getUISessionTimeoutMillis(): Promise<number> {
    const { exists, value } = await AuthApi.getConfigByName(
      'lcm.ui.session-inactivity-timeout-milliseconds'
    );
    return exists && value
      ? +value
      : CommonNumericConstants.DefaultSessionInactivityTimeInMillis;
  }

  public static async logout() {
    await Http.post(AuthznApiUri.LOGOUT_URL);
  }

  public static async getUserData(): Promise<UiUserData> {
    try {
      const user = await this.whoAmI();
      return { loggedIn: true, user };
    } catch (e) {
      return { loggedIn: false };
    }
  }

  public static async updateFirstBootPassword(
    username: string,
    password: string
  ) {
    await Http.put(AuthznApiUri.UPDATE_FIRST_BOOT_PASSWORD_URL, {
      username,
      password
    });
  }

  public static async triggerVidmClusterHealth() {
    const response = await Http.get<Request>(
      AuthznApiUri.TRIGGER_CLUSTER_HEALTH_URL
    );
    return response.data.vmid;
  }

  public static async updateLocalUserPassword(
    username: string,
    password: string
  ) {
    await Http.put(AuthznApiUri.UPDATE_LOCAL_USER_PASSWORD_URL, {
      username,
      password
    });
  }
}
